import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { patientService } from '../../services/patientService';
import { Patient, ApiResponse } from '../../types';
import PatientModal from '../../components/organisms/PatientModal';
import PatientImage from '../../components/atoms/PatientImage';
import { PageHeader } from '../../components/molecules';

const Patients: React.FC = () => {
  const { t } = useTranslation();
  const [patients, setPatients] = useState<Patient[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSpecies, setSelectedSpecies] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);
  const [deleteLoading, setDeleteLoading] = useState<string | null>(null);

  // Fetch patients on component mount
  useEffect(() => {
    fetchPatients();
  }, []);

  const fetchPatients = async (search?: string, species?: string) => {
    try {
      setLoading(true);
      setError(null);

      const query: any = {};
      if (search) query.search = search;
      if (species) query.species = species;

      const response: ApiResponse<{ patients: Patient[]; total: number; page: number; totalPages: number }> =
        await patientService.getPatients(query);

      if (response.success && response.data && response.data.patients) {
        setPatients(response.data.patients);
      } else {
        setPatients([]);
        setError(response.message || 'Failed to fetch patients');
      }
    } catch (err: any) {
      setError(err.message || 'An error occurred while fetching patients');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    fetchPatients(searchTerm, selectedSpecies);
  };

  const handleSpeciesFilter = (species: string) => {
    setSelectedSpecies(species);
    fetchPatients(searchTerm, species);
  };

  // Helper function to calculate age
  const calculateAge = (birthDate?: string): string => {
    if (!birthDate) return 'Unknown';

    const birth = new Date(birthDate);
    const today = new Date();
    const diffTime = Math.abs(today.getTime() - birth.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays < 30) {
      return `${diffDays} days`;
    } else if (diffDays < 365) {
      const months = Math.floor(diffDays / 30);
      return `${months} month${months > 1 ? 's' : ''}`;
    } else {
      const years = Math.floor(diffDays / 365);
      const remainingMonths = Math.floor((diffDays % 365) / 30);
      if (remainingMonths > 0) {
        return `${years} year${years > 1 ? 's' : ''} ${remainingMonths} month${remainingMonths > 1 ? 's' : ''}`;
      }
      return `${years} year${years > 1 ? 's' : ''}`;
    }
  };

  // Helper function to get species icon
  const getSpeciesIcon = (species: string): string => {
    switch (species.toLowerCase()) {
      case 'dog':
        return '🐕';
      case 'cat':
        return '🐈';
      case 'bird':
        return '🦜';
      case 'rabbit':
        return '🐰';
      case 'hamster':
        return '🐹';
      default:
        return '🐾';
    }
  };

  // CRUD operations
  const handleAddPatient = () => {
    setSelectedPatient(null);
    setModalOpen(true);
  };

  const handleEditPatient = (patient: Patient) => {
    setSelectedPatient(patient);
    setModalOpen(true);
  };

  const handleDeletePatient = async (patientId: string) => {
    if (!window.confirm('Are you sure you want to delete this patient? This action cannot be undone.')) {
      return;
    }

    try {
      setDeleteLoading(patientId);
      const response: ApiResponse = await patientService.deletePatient(patientId);

      if (response.success) {
        setPatients(patients.filter(p => p.id !== patientId));
      } else {
        setError(response.message || 'Failed to delete patient');
      }
    } catch (err: any) {
      setError(err.message || 'An error occurred while deleting patient');
    } finally {
      setDeleteLoading(null);
    }
  };

  const handleModalSuccess = () => {
    fetchPatients(searchTerm, selectedSpecies);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedPatient(null);
  };

  const totalPatients = patients.length;
  const activeFilters = Number(Boolean(searchTerm)) + Number(Boolean(selectedSpecies));
  const selectedSpeciesLabel = selectedSpecies
    ? t(`patients.filterOptions.${selectedSpecies}`, { defaultValue: selectedSpecies })
    : null;
  const filterSummary = activeFilters
    ? [
        searchTerm ? `${t('patients.filters.search', { defaultValue: 'Search' })}: "${searchTerm}"` : null,
        selectedSpeciesLabel ? `${t('patients.speciesLabel', { defaultValue: 'Species' })}: ${selectedSpeciesLabel}` : null
      ]
        .filter(Boolean)
        .join(' · ')
    : t('patients.filters.none', { defaultValue: 'Showing every patient' });

  return (
    <div className="space-y-8">
      <PageHeader
        eyebrow={t('patients.headerEyebrow', { defaultValue: 'Patient operations' })}
        title={t('patients.title')}
        description={t('patients.subtitle')}
        icon="🐾"
        accent="indigo"
        actions={(
          <button
            onClick={handleAddPatient}
            className="inline-flex items-center gap-2 rounded-full bg-brand-primary px-5 py-2.5 text-sm font-semibold text-white shadow-soft transition hover:bg-brand-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-accent"
          >
            <span className="text-lg leading-none">➕</span>
            {t('patients.addButton')}
          </button>
        )}
        stats={[
          {
            label: t('patients.stats.totalLabel', { defaultValue: 'Patients' }),
            value: totalPatients.toLocaleString(),
            helper: t('patients.stats.totalHelper', { defaultValue: 'Synced records in view' })
          },
          {
            label: t('patients.stats.filtersLabel', { defaultValue: 'Filters' }),
            value:
              activeFilters > 0
                ? t('patients.stats.filtersActive', {
                    defaultValue: '{{count}} active',
                    count: activeFilters
                  })
                : t('patients.stats.filtersIdle', { defaultValue: 'None applied' }),
            helper: filterSummary
          }
        ]}
      >
        <form onSubmit={handleSearch} className="grid gap-4 md:grid-cols-[minmax(0,1fr)_220px]">
          <label className="flex flex-col gap-2 text-sm font-semibold text-slate-600">
            {t('patients.searchLabel')}
            <input
              type="text"
              placeholder={t('patients.searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700 placeholder:text-slate-400 focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/20"
            />
          </label>

          <label className="flex flex-col gap-2 text-sm font-semibold text-slate-600">
            {t('patients.speciesLabel')}
            <select
              value={selectedSpecies}
              onChange={(e) => handleSpeciesFilter(e.target.value)}
              className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700 focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/20"
            >
              <option value="">{t('patients.filterOptions.all')}</option>
              <option value="dog">{t('patients.filterOptions.dog')}</option>
              <option value="cat">{t('patients.filterOptions.cat')}</option>
              <option value="bird">{t('patients.filterOptions.bird')}</option>
              <option value="other">{t('patients.filterOptions.other')}</option>
            </select>
          </label>
        </form>
      </PageHeader>

      <section className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-soft">
        <div className="grid border-b border-slate-200 bg-slate-50 px-6 py-4 text-xs font-semibold uppercase tracking-[0.3em] text-slate-500 md:grid-cols-[1.2fr_1fr_1fr_1fr_0.6fr_0.5fr]">
          <span>{t('patients.table.name')}</span>
          <span>{t('patients.table.species')}</span>
          <span>{t('patients.table.breed')}</span>
          <span>{t('patients.table.owner')}</span>
          <span>{t('patients.table.age')}</span>
          <span className="hidden text-right md:block">{t('patients.table.actions')}</span>
        </div>
        {/* Content */}
        {loading ? (
          <div className="px-6 py-10 text-center text-sm text-slate-500">
            <div className="flex items-center justify-center space-x-2">
              <div className="h-4 w-4 animate-spin rounded-full border-2 border-brand-primary border-t-transparent"></div>
              <span>Loading patients...</span>
            </div>
          </div>
        ) : error ? (
          <div className="px-6 py-10 text-center">
            <div className="text-sm text-red-600 mb-4">{error}</div>
            <button
              onClick={() => fetchPatients()}
              className="inline-flex items-center gap-2 rounded-full bg-brand-primary px-4 py-2 text-sm font-semibold text-white shadow-soft transition hover:bg-brand-secondary"
            >
              Try Again
            </button>
          </div>
        ) : !patients || patients.length === 0 ? (
          <div className="px-6 py-10 text-center text-sm text-slate-500">
            {t('patients.table.empty')}
          </div>
        ) : (
          <div className="divide-y divide-slate-200">
            {patients?.map((patient) => (
              <div key={patient.id} className="px-6 py-4 hover:bg-slate-50 transition-colors">
                <div className="grid items-center gap-4 md:grid-cols-[1.2fr_1fr_1fr_1fr_0.6fr_0.5fr]">
                  {/* Patient Name & Photo */}
                  <div className="flex items-center gap-3">
                    <PatientImage
                      patient={patient}
                      size="md"
                    />
                    <div>
                      <div className="font-medium text-slate-800">{patient.name}</div>
                      <div className="text-xs text-slate-500">
                        {patient.gender === 'MALE' ? 'Male' : 'Female'} • {patient.microchipId || 'No Microchip'}
                      </div>
                    </div>
                  </div>

                  {/* Species */}
                  <div className="text-sm text-slate-600 capitalize">
                    {getSpeciesIcon(patient.species)} {patient.species}
                  </div>

                  {/* Breed */}
                  <div className="text-sm text-slate-600">
                    {patient.breed || 'Unknown'}
                  </div>

                  {/* Owner */}
                  <div className="text-sm text-slate-600">
                    {patient.owner?.name || 'Unknown Owner'}
                  </div>

                  {/* Age */}
                  <div className="text-sm text-slate-600">
                    {calculateAge(patient.birthDate)}
                  </div>

                  {/* Actions */}
                  <div className="flex items-center justify-end gap-2">
                    <button
                      onClick={() => handleEditPatient(patient)}
                      className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-slate-100 text-slate-600 hover:bg-brand-primary hover:text-white transition-colors"
                      title="Edit Patient"
                    >
                      <span className="text-sm">✏️</span>
                    </button>
                    <button
                      onClick={() => handleDeletePatient(patient.id)}
                      disabled={deleteLoading === patient.id}
                      className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-slate-100 text-slate-600 hover:bg-red-500 hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Delete Patient"
                    >
                      {deleteLoading === patient.id ? (
                        <div className="h-3 w-3 animate-spin rounded-full border border-current border-t-transparent"></div>
                      ) : (
                        <span className="text-sm">🗑️</span>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Patient Modal */}
      <PatientModal
        isOpen={modalOpen}
        onClose={handleCloseModal}
        patient={selectedPatient}
        onSuccess={handleModalSuccess}
      />
    </div>
  );
};

export default Patients;
